/**
 * Extract Apple Music track ID and country from various Apple Music URL formats
 * Examples:
 * - https://music.apple.com/us/album/track-name/1234567890?i=9876543210
 * - https://music.apple.com/gb/song/track-name/9876543210
 * - https://itunes.apple.com/us/album/track-name/id1234567890?i=9876543210
 */
export const getAppleMusicTrackInfo = (
  url: string,
): { trackId: string; country: string } | null => {
  // Extract just the URL part (first whitespace-separated token) to handle trailing text
  const cleanUrl = url.trim().split(/\s+/)[0]

  // Handle music.apple.com URLs
  const appleMusicMatch = cleanUrl.match(
    /music\.apple\.com\/([a-z]{2})\/(?:album|song)\/[^/]+\/(?:id)?(\d+)(?:\?i=(\d+))?/,
  )
  if (appleMusicMatch) {
    const country = appleMusicMatch[1]
    const trackId = appleMusicMatch[3] || appleMusicMatch[2] // Use i= parameter if available, otherwise main ID
    return { trackId, country }
  }

  // Handle legacy itunes.apple.com URLs
  const itunesMatch = cleanUrl.match(
    /itunes\.apple\.com\/([a-z]{2})\/(?:album|song)\/[^/]+\/id(\d+)(?:\?i=(\d+))?/,
  )
  if (itunesMatch) {
    const country = itunesMatch[1]
    const trackId = itunesMatch[3] || itunesMatch[2] // Use i= parameter if available, otherwise main ID
    return { trackId, country }
  }

  return null
}

/**
 * Check if a URL is an Apple Music URL
 */
export const isAppleMusicUrl = (url: string): boolean => {
  // Extract just the URL part to handle trailing text
  const cleanUrl = url.trim().split(/\s+/)[0]

  return (
    getAppleMusicTrackInfo(url) !== null ||
    cleanUrl.includes('music.apple.com') ||
    cleanUrl.includes('itunes.apple.com')
  )
}

/**
 * Generate Apple Music embed URL from track info
 */
export const getAppleMusicEmbedUrl = (
  trackId: string,
  albumId?: string,
  country: string = 'us',
): string => {
  if (albumId) {
    // For album tracks with specific track ID
    return `https://embed.music.apple.com/${country}/album/${albumId}?i=${trackId}`
  } else {
    // For standalone songs
    return `https://embed.music.apple.com/${country}/song/${trackId}`
  }
}

/**
 * Get Apple Music preview URL using iTunes Search API
 * This returns the 30-second preview
 */
export const getAppleMusicPreviewUrl = async (
  trackId: string,
  country: string = 'us',
): Promise<string | null> => {
  try {
    // Use iTunes Search API to get track information including preview URL
    const response = await fetch(
      `https://itunes.apple.com/lookup?id=${trackId}&country=${country}&entity=song`,
    )
    const data = await response.json()

    if (data.results && data.results.length > 0) {
      const track = data.results[0]
      return track.previewUrl || null
    }

    return null
  } catch (error) {
    console.error('Failed to fetch Apple Music preview URL:', error)
    return null
  }
}
